import type { GifEmbedAssetState } from '../../store/index.types';
import type { GifEmbedLoadStatus } from '../../types';
import { KLIPY_API_KEY } from 'Constants/env';
import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useUIStore } from '~/modules/ui/index.store';
import {
  fetchKlipyGifItemById,
  getKlipyGifChatDisplayUrl,
  getKlipyGifChatVideoMp4Url,
  getKlipyGifChatVideoWebmUrl,
  getKlipyGifPosterUrl,
} from '../../requests';
import {
  DEFAULT_GIF_EMBED_ASSET,
  EMPTY_GIF_ID_ASSET,
} from '../../store/index.slice';
import { selectGifEmbedAsset } from '../../store/selectGifEmbedAsset';

export function useGifEmbedAsset(gifId: string): {
  status: GifEmbedLoadStatus;
  imageUrl: string | null;
  mp4Url: string | null;
  webmUrl: string | null;
  posterUrl: string | null;
  altText: string;
  useVideo: boolean;
} {
  const trimmedId = gifId.trim();

  const asset: GifEmbedAssetState = useSyncExternalStore(
    useUIStore.subscribe,
    () => selectGifEmbedAsset(useUIStore.getState(), trimmedId),
    () =>
      !trimmedId ? EMPTY_GIF_ID_ASSET : DEFAULT_GIF_EMBED_ASSET
  );

  const setGifEmbedAsset = useCallback(
    (id: string, partial: Partial<GifEmbedAssetState>) => {
      useUIStore.getState().setGifEmbedAsset(id, partial);
    },
    []
  );

  useEffect(() => {
    if (!KLIPY_API_KEY) {
      setGifEmbedAsset(gifId, { status: 'unavailable' });
      return;
    }

    if (!trimmedId) {
      setGifEmbedAsset(gifId, { status: 'unavailable' });
      return;
    }

    const controller = new AbortController();
    const apiKey = KLIPY_API_KEY.trim();

    setGifEmbedAsset(gifId, { ...DEFAULT_GIF_EMBED_ASSET });

    void (async () => {
      try {
        const item = await fetchKlipyGifItemById(
          apiKey,
          trimmedId,
          controller.signal
        );

        if (controller.signal.aborted) {
          return;
        }

        if (!item) {
          setGifEmbedAsset(gifId, { status: 'unavailable' });
          return;
        }

        const mp4 = getKlipyGifChatVideoMp4Url(item);
        const webm = getKlipyGifChatVideoWebmUrl(item);
        const gif = getKlipyGifChatDisplayUrl(item);
        const poster = getKlipyGifPosterUrl(item);
        if (mp4 || webm) {
          setGifEmbedAsset(gifId, {
            mp4Url: mp4 ?? null,
            webmUrl: webm ?? null,
            posterUrl: poster ?? null,
            imageUrl: null,
          });
        } else {
          if (!gif) {
            setGifEmbedAsset(gifId, { status: 'unavailable' });
            return;
          }
          setGifEmbedAsset(gifId, { imageUrl: gif });
        }

        const title = item.title?.trim();

        if (title) {
          setGifEmbedAsset(gifId, { altText: title });
        }

        setGifEmbedAsset(gifId, { status: 'ready' });
      } catch {
        if (controller.signal.aborted) {
          return;
        }
        setGifEmbedAsset(gifId, { status: 'unavailable' });
      }
    })();

    return () => {
      controller.abort();
    };
  }, [gifId, trimmedId, setGifEmbedAsset]);

  const useVideo = useMemo(
    () => Boolean(asset.mp4Url || asset.webmUrl),
    [asset.mp4Url, asset.webmUrl]
  );

  return {
    status: asset.status,
    imageUrl: asset.imageUrl,
    mp4Url: asset.mp4Url,
    webmUrl: asset.webmUrl,
    posterUrl: asset.posterUrl,
    altText: asset.altText,
    useVideo,
  };
}
